import InfoIcon from '@mui/icons-material/Info';
import { type Filter } from 'lib/common/GlobalSearcher';
import { formatDate } from 'lib/functions/utils';
import React from 'react';

export const PAGE_SIZE = 10;

export type ExtensionsFilters = {
  startDueDate: string | null;
  endDueDate: string | null;
  startCreatedAt: string | null;
  endCreatedAt: string | null;
  startActivationDate: string | null;
  endActivationDate: string | null;
  businessIdentifier: string;
  businessName: string;
  stakeholderIdentifier: string;
  stakeholderName: string;
  folio: string;
  paid: string;
  status: string;
};

export const INITIAL_FILTERS: ExtensionsFilters = {
  startDueDate: null,
  endDueDate: null,
  startCreatedAt: null,
  endCreatedAt: null,
  startActivationDate: null,
  endActivationDate: null,
  businessIdentifier: '',
  businessName: '',
  stakeholderIdentifier: '',
  stakeholderName: '',
  folio: '',
  paid: 'ALL',
  status: '',
};

type BuildQueryParams = {
  page: number;
  pageSize: number;
  all?: boolean;
};

export const buildExtensionsQuery = (
  filters: ExtensionsFilters,
  { page, pageSize, all }: BuildQueryParams,
): string => {
  const params = new URLSearchParams();

  if (filters.startDueDate) params.append('startDueDate', filters.startDueDate);
  if (filters.endDueDate) params.append('endDueDate', filters.endDueDate);
  if (filters.startCreatedAt)
    params.append('startCreatedAt', filters.startCreatedAt);
  if (filters.endCreatedAt) params.append('endCreatedAt', filters.endCreatedAt);
  if (filters.startActivationDate)
    params.append('startActivationDate', filters.startActivationDate);
  if (filters.endActivationDate)
    params.append('endActivationDate', filters.endActivationDate);
  if (filters.businessIdentifier)
    params.append('businessIdentifier', filters.businessIdentifier);
  if (filters.businessName) params.append('businessName', filters.businessName);
  if (filters.stakeholderIdentifier)
    params.append('stakeholderIdentifier', filters.stakeholderIdentifier);
  if (filters.stakeholderName)
    params.append('stakeholderName', filters.stakeholderName);
  if (filters.folio) params.append('folio', filters.folio);
  if (filters.paid) params.append('paid', filters.paid);
  if (page) {
    params.append('page', String(page));
    params.append('pageSize', String(pageSize));
  }
  if (filters.status) params.append('status', filters.status);
  if (all) params.append('all', 'true');

  return params.toString();
};

type HeaderConfig = {
  userIsExpress: boolean;
  onBusinessClick: (id: string) => void;
  onStakeholderClick: (id: string) => void;
};

export const getExtensionsHeaders = ({
  userIsExpress,
  onBusinessClick,
  onStakeholderClick,
}: HeaderConfig) => {
  const base = [
    { key: 'id', label: 'ID', type: 'numeric-id' },
    { key: 'folio', label: 'Folio documento' },
    { key: 'businessName', label: 'Cliente' },
    userIsExpress
      ? { key: 'businessIdentifier', label: 'Rut' }
      : {
          key: 'businessIdentifier',
          label: 'Rut',
          handleAction: onBusinessClick,
          icon: <InfoIcon fontSize="small" />,
        },
    { key: 'stakeholderName', label: 'Deudor' },
    userIsExpress
      ? { key: 'stakeholderIdentifier', label: 'Rut' }
      : {
          key: 'stakeholderIdentifier',
          label: 'Rut',
          handleAction: onStakeholderClick,
          icon: <InfoIcon fontSize="small" />,
        },
    { key: 'paidLabel', label: 'Pagada', type: 'status-chip' },
    { key: 'status', label: 'Estado', type: 'status-chip' },
    { key: 'documentAmount', label: 'Monto documento', type: 'money' },
    { key: 'operationCostFixed', label: 'Costo fijo', type: 'money' },
    { key: 'operationCostVariable', label: 'Costo variable', type: 'money' },
    { key: 'rateCost', label: 'Costo tasa', type: 'money' },
    {
      key: 'paymentDate',
      label: 'Vcto. original',
      type: 'date',
      formatter: (value: Date) => formatDate(value, 'dd-mm-yyyy'),
    },
    {
      key: 'dueDate',
      label: 'Vcto. prórroga',
      type: 'date',
      formatter: (value: Date) => formatDate(value, 'dd-mm-yyyy'),
    },
    {
      key: 'createdAt',
      label: 'Creado en',
      type: 'date',
      formatter: (value: Date) => formatDate(value, 'dd-mm-yyyy'),
    },
    {
      key: 'startDate',
      label: 'Activación',
      type: 'date',
      formatter: (value: Date) => formatDate(value, 'dd-mm-yyyy'),
    },
    { key: 'executives', label: 'Ejecutivo' },
  ];

  return base as {
    key: string;
    label: string;
    type?: string;
    formatter?: (value: Date) => string;
    handleAction?: (id: string) => void;
    icon?: React.ReactNode;
  }[];
};

export const getExtensionsFiltersConfig = (
  filters: ExtensionsFilters,
  setFilters: React.Dispatch<React.SetStateAction<ExtensionsFilters>>,
): Filter[] => [
  {
    label: 'Estado',
    type: 'select',
    value: filters.status,
    setter: (value: string) =>
      setFilters((prev) => ({ ...prev, status: value })),
    options: [
      { label: 'Activa', value: 'ACTIVE' },
      { label: 'Completada', value: 'COMPLETED' },
      { label: 'Todas', value: 'ALL' },
    ],
  },
  {
    label: 'Pagada',
    type: 'select',
    value: filters.paid,
    setter: (value: string) => setFilters((prev) => ({ ...prev, paid: value })),
    options: [
      { label: 'Todas', value: 'ALL' },
      { label: 'Pagada', value: 'true' },
      { label: 'No pagada', value: 'false' },
    ],
  },
  {
    label: 'Folio',
    type: 'text',
    value: filters.folio,
    setter: (value: string) =>
      setFilters((prev) => ({ ...prev, folio: value })),
  },
  {
    label: 'Fecha de nuevo vencimiento',
    type: 'date',
    value: filters.startDueDate,
    setter: (value: string | null) =>
      setFilters((prev) => ({ ...prev, startDueDate: value })),
    dateKey: 'startDueDate',
    dateType: 'due',
    pairKey: 'endDueDate',
  },
  {
    label: 'Fecha de nuevo vencimiento',
    type: 'date',
    value: filters.endDueDate,
    setter: (value: string | null) =>
      setFilters((prev) => ({ ...prev, endDueDate: value })),
    dateKey: 'endDueDate',
    dateType: 'due',
    pairKey: 'startDueDate',
  },
  {
    label: 'Fecha de creación',
    type: 'date',
    value: filters.startCreatedAt,
    setter: (value: string | null) =>
      setFilters((prev) => ({ ...prev, startCreatedAt: value })),
    dateKey: 'startCreatedAt',
    dateType: 'created',
    pairKey: 'endCreatedAt',
  },
  {
    label: 'Fecha de creación',
    type: 'date',
    value: filters.endCreatedAt,
    setter: (value: string | null) =>
      setFilters((prev) => ({ ...prev, endCreatedAt: value })),
    dateKey: 'endCreatedAt',
    dateType: 'created',
    pairKey: 'startCreatedAt',
  },
  {
    label: 'Fecha de activación',
    type: 'date',
    value: filters.startActivationDate,
    setter: (value: string | null) =>
      setFilters((prev) => ({ ...prev, startActivationDate: value })),
    dateKey: 'startActivationDate',
    dateType: 'activation',
    pairKey: 'endActivationDate',
  },
  {
    label: 'Fecha de activación',
    type: 'date',
    value: filters.endActivationDate,
    setter: (value: string | null) =>
      setFilters((prev) => ({ ...prev, endActivationDate: value })),
    dateKey: 'endActivationDate',
    dateType: 'activation',
    pairKey: 'startActivationDate',
  },
  {
    label: 'Nombre cliente',
    type: 'autocomplete',
    value: filters.businessName,
    key: 'businessName',
    setter: (value: string) =>
      setFilters((prev) => ({ ...prev, businessName: value })),
  },
  {
    label: 'Rut emisor',
    type: 'text',
    value: filters.businessIdentifier,
    setter: (value: string) =>
      setFilters((prev) => ({ ...prev, businessIdentifier: value })),
  },
  {
    label: 'Nombre deudor',
    type: 'autocomplete',
    value: filters.stakeholderName,
    key: 'stakeholderName',
    setter: (value: string) =>
      setFilters((prev) => ({ ...prev, stakeholderName: value })),
  },
  {
    label: 'Rut contraparte',
    type: 'text',
    value: filters.stakeholderIdentifier,
    setter: (value: string) =>
      setFilters((prev) => ({ ...prev, stakeholderIdentifier: value })),
  },
];
